import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { errorMessage } from '../../lib/api';
import { useAuth } from '../../lib/auth-context-value';
import { leadViews } from '../../lib/resources';
import type { SavedView } from '../../types';
import { Button, ConfirmDialog, ErrorNote, Field, Modal, Toggle, inputClass } from '../ui';

/**
 * Dropdown "Visões" (doc 02, 1.4): busca, escolher aplica os filtros da visão,
 * ✏ renomeia, 🗑 exclui. "Salvar como visão" guarda os filtros que estão na tela.
 */
export function ViewsMenu({
  activeViewId,
  currentQuery,
  onSelect,
}: {
  activeViewId: string | null;
  /** Querystring dos filtros aplicados agora (sem a visão). */
  currentQuery: string;
  onSelect: (view: SavedView | null) => void;
}) {
  const queryClient = useQueryClient();
  const { session, isAdmin } = useAuth();
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [saving, setSaving] = useState(false);
  const [renaming, setRenaming] = useState<SavedView | null>(null);
  const [removing, setRemoving] = useState<SavedView | null>(null);

  const query = useQuery({ queryKey: ['lead-views'], queryFn: leadViews.list });
  const views = query.data?.data ?? [];
  const active = views.find((view) => view.id === activeViewId) ?? null;
  const visible = views.filter((view) => view.name.toLowerCase().includes(term.toLowerCase()));
  const canEdit = (view: SavedView) =>
    isAdmin || !view.createdById || view.createdById === session?.user.id;
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['lead-views'] });

  const remove = useMutation({
    mutationFn: (view: SavedView) => leadViews.remove(view.id),
    onSuccess: (_, view) => {
      setRemoving(null);
      invalidate();
      if (view.id === activeViewId) onSelect(null);
    },
  });

  return (
    <div className="relative">
      <Button onClick={() => setOpen(!open)}>
        {active ? `📁 ${active.name}` : 'Visões'} ▾
      </Button>
      {open && (
        <div className="absolute left-0 z-30 mt-1 w-72 rounded-xl border border-slate-200 bg-white p-2 shadow-xl">
          <input
            autoFocus
            className={`${inputClass} mt-0`}
            placeholder="Buscar visão..."
            value={term}
            onChange={(event) => setTerm(event.target.value)}
          />
          <ul className="mt-2 max-h-64 overflow-y-auto">
            {visible.length === 0 && (
              <li className="px-2 py-2 text-xs text-slate-400">Nenhuma visão.</li>
            )}
            {visible.map((view) => (
              <li
                key={view.id}
                className="flex items-center gap-1 rounded-lg px-2 py-1.5 hover:bg-slate-50"
              >
                <button
                  type="button"
                  className="min-w-0 flex-1 truncate text-left text-sm text-slate-700"
                  onClick={() => {
                    onSelect(view);
                    setOpen(false);
                  }}
                >
                  {view.name}
                  {!view.isShared && (
                    <span className="ml-1 text-[10px] text-slate-400">(pessoal)</span>
                  )}
                </button>
                {canEdit(view) && (
                  <>
                    <button
                      type="button"
                      className="text-xs text-slate-400"
                      onClick={() => setRenaming(view)}
                      aria-label="Renomear"
                    >
                      ✏
                    </button>
                    <button
                      type="button"
                      className="text-xs text-slate-400"
                      onClick={() => setRemoving(view)}
                      aria-label="Excluir"
                    >
                      🗑
                    </button>
                  </>
                )}
              </li>
            ))}
          </ul>
          <div className="mt-2 flex gap-2 border-t border-slate-100 pt-2">
            {active && (
              <Button
                variant="ghost"
                onClick={() => {
                  onSelect(null);
                  setOpen(false);
                }}
              >
                Sair da visão
              </Button>
            )}
            <Button
              variant="primary"
              disabled={!currentQuery}
              onClick={() => {
                setSaving(true);
                setOpen(false);
              }}
            >
              Salvar filtros como visão
            </Button>
          </div>
        </div>
      )}

      {saving && (
        <ViewForm
          title="Salvar como visão"
          initialName=""
          onClose={() => setSaving(false)}
          onSubmit={async (name, isShared) => {
            const view = await leadViews.create({ name, query: currentQuery, isShared });
            invalidate();
            onSelect(view);
          }}
        />
      )}
      {renaming && (
        <ViewForm
          title="Atualizar visão"
          initialName={renaming.name}
          initialShared={renaming.isShared}
          onClose={() => setRenaming(null)}
          onSubmit={async (name, isShared) => {
            await leadViews.update(renaming.id, { name, isShared });
            invalidate();
          }}
        />
      )}
      {removing && (
        <ConfirmDialog
          title="Excluir visão?"
          message={
            <p>
              <strong>{removing.name}</strong> vai para a Lixeira. Os leads não mudam.
            </p>
          }
          confirmLabel="Excluir"
          danger
          busy={remove.isPending}
          error={remove.error ? errorMessage(remove.error) : null}
          onConfirm={() => remove.mutate(removing)}
          onClose={() => setRemoving(null)}
        />
      )}
    </div>
  );
}

function ViewForm({
  title,
  initialName,
  initialShared = true,
  onSubmit,
  onClose,
}: {
  title: string;
  initialName: string;
  initialShared?: boolean;
  onSubmit: (name: string, isShared: boolean) => Promise<void>;
  onClose: () => void;
}) {
  const [name, setName] = useState(initialName);
  const [isShared, setIsShared] = useState(initialShared);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  return (
    <Modal title={title} onClose={onClose}>
      <form
        className="space-y-4"
        onSubmit={async (event) => {
          event.preventDefault();
          setBusy(true);
          setError(null);
          try {
            await onSubmit(name.trim(), isShared);
            onClose();
          } catch (submitError) {
            setError(errorMessage(submitError, 'Não foi possível salvar a visão'));
          } finally {
            setBusy(false);
          }
        }}
      >
        <Field label="Nome *">
          <input
            required
            autoFocus
            className={inputClass}
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </Field>
        <Toggle
          label="Compartilhar com a empresa"
          hint="Desligado, só você vê esta visão."
          checked={isShared}
          onChange={setIsShared}
        />
        {error && <ErrorNote>{error}</ErrorNote>}
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancelar</Button>
          <Button type="submit" variant="primary" disabled={busy || !name.trim()}>
            Confirmar
          </Button>
        </div>
      </form>
    </Modal>
  );
}
